import type { BlogPost } from "@/types/blogTypes";

export interface BlogPostsQueryOptions {
  limit?: number;
  published?: boolean | "all";
  orderBy?: string;
  random?: boolean;
}

export interface PreloadedState {
  blogPostsByQuery: Record<string, BlogPost[]>;
  blogPostsBySlug: Record<string, BlogPost | null>;
}

declare global {
  interface Window {
    __PRELOADED_STATE__?: PreloadedState;
  }

  // eslint-disable-next-line no-var
  var __ELF_PRELOADED_STATE__: PreloadedState | undefined;
}

function normalizeState(state?: Partial<PreloadedState> | null): PreloadedState {
  return {
    blogPostsByQuery: state?.blogPostsByQuery ?? {},
    blogPostsBySlug: state?.blogPostsBySlug ?? {},
  };
}

export function createEmptyPreloadedState(): PreloadedState {
  return normalizeState();
}

export function getBlogPostsQueryKey(options: BlogPostsQueryOptions = {}): string {
  return JSON.stringify({
    limit: options.limit ?? null,
    orderBy: options.orderBy ?? null,
    published: options.published ?? null,
    random: options.random ?? false,
  });
}

export function getPreloadedState(): PreloadedState {
  if (typeof window !== "undefined") {
    return normalizeState(window.__PRELOADED_STATE__);
  }

  return normalizeState(globalThis.__ELF_PRELOADED_STATE__);
}

export function setServerPreloadedState(state: PreloadedState): void {
  globalThis.__ELF_PRELOADED_STATE__ = normalizeState(state);
}

export function clearServerPreloadedState(): void {
  delete globalThis.__ELF_PRELOADED_STATE__;
}

export function readPreloadedBlogPosts(
  options: BlogPostsQueryOptions = {},
): BlogPost[] | null {
  const state = getPreloadedState();
  return state.blogPostsByQuery[getBlogPostsQueryKey(options)] ?? null;
}

export function readPreloadedBlogPost(slug?: string): BlogPost | null {
  if (!slug) return null;
  const state = getPreloadedState();
  return state.blogPostsBySlug[slug] ?? null;
}

export function serializePreloadedState(state: PreloadedState): string {
  return JSON.stringify(normalizeState(state)).replace(/</g, "\\u003c");
}
